<script setup lang="ts">
  import { onClickOutside } from '@vueuse/core'
  import type { FunctionalComponent } from 'vue'
  import { computed, ref } from 'vue'
  import type { MaybeReadonly } from '~shared/util/type'

  interface BaseDropdownOption {
    value: string
  }

  interface IconOnlyDropdownOption extends BaseDropdownOption {
    icon: FunctionalComponent | string
    label?: undefined
  }

  interface LabelOnlyDropdownOption extends BaseDropdownOption {
    label: string
    icon?: undefined
  }

  interface LabelIconDropdownOption extends BaseDropdownOption {
    label: string
    icon: FunctionalComponent | string
  }

  export type DropdownOption =
    | IconOnlyDropdownOption
    | LabelOnlyDropdownOption
    | LabelIconDropdownOption

  export type DropdownProps = {
    items?: MaybeReadonly<DropdownOption[]>
    modelValue?: string | string[]
    multiselect?: boolean
    showChevron?: boolean
    label?: string | null
    icon?: FunctionalComponent | string | null
  }

  const props = withDefaults(defineProps<DropdownProps>(), {
    items: () => [],
    showChevron: true,
    multiselect: false,
  })

  const emit = defineEmits<{
    'update:modelValue': [value: string | string[]]
  }>()

  const isOpen = ref(false)
  const target = ref(null)

  onClickOutside(target, () => (isOpen.value = false))

  const toggle = () => {
    isOpen.value = !isOpen.value
  }

  const handleSelect = (item: DropdownOption) => {
    if (props.multiselect) {
      const current = Array.isArray(props.modelValue) ? props.modelValue : []
      const idx = current.indexOf(item.value)
      if (idx > -1) {
        emit(
          'update:modelValue',
          current.filter((v: any) => v !== item.value),
        )
      } else {
        emit('update:modelValue', [...current, item.value])
      }
    } else {
      emit('update:modelValue', item.value)
      isOpen.value = false
    }
  }

  const isSelected = (item: DropdownOption) => {
    if (props.multiselect)
      return Array.isArray(props.modelValue) && props.modelValue.includes(item.value)

    return props.modelValue === item.value
  }

  // Computed Label for the Header
  const label = computed(() => {
    // Opt out of any label if null is passed
    if (props.label === null) return
    // Override any selected item if a label is passed
    if (props.label) return props.label

    // If single select, show the selected item's label
    if (!props.multiselect && props.modelValue) {
      const selected = props.items.find((i) => i.value === props.modelValue)
      if (selected) return selected.label
    }

    // If multiselect, maybe show "X Selected"?
    if (props.multiselect && Array.isArray(props.modelValue) && props.modelValue.length > 0) {
      return `${props.modelValue.length} Selected`
    }
  })

  // Computed Icon for the Header
  const icon = computed(() => {
    // Allow disabling the icon completely
    if (props.icon === null) return
    // Override any selected item's icon if an icon is passed
    if (props.icon) return props.icon

    // If single select, show the selected item's icon
    if (!props.multiselect && props.modelValue) {
      const selected = props.items.find((i) => i.value === props.modelValue)
      if (selected && selected.icon) return selected.icon
    }

    return
  })
</script>

<template>
  <div ref="target" class="relative inline-block text-left">
    <button
      type="button"
      @click="toggle"
      class="corner-squircle bg-tan-900 hover:bg-tan-800 flex size-full min-w-max cursor-pointer items-center justify-between gap-3 rounded-xl px-2 py-2 text-sm font-bold text-white transition-colors"
      :class="{ 'ring-2 ring-white/20': isOpen }"
    >
      <div class="flex items-center gap-3">
        <div v-if="$slots.icon || icon" class="flex items-center">
          <slot name="icon">
            <component :is="icon" v-if="icon" class="size-5" />
          </slot>
        </div>

        <span class="mt-0.5" v-if="label">
          <slot>
            {{ label }}
          </slot>
        </span>
      </div>

      <i-fa7-solid-chevron-down
        v-if="showChevron"
        class="ml-2 text-white/50 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <transition name="slide-in-out">
      <div
        v-if="isOpen && items.length > 0"
        class="corner-squircle bg-tan-900 ring-opacity-5 absolute right-0 z-50 mt-2 w-full min-w-[200px] origin-top-right overflow-hidden rounded-xl shadow-xl ring-1 ring-black focus:outline-none"
      >
        <button
          v-for="item in items"
          :key="item.value"
          @click="handleSelect(item)"
          class="group hover:bg-tan-800 relative flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left text-sm text-white transition-colors"
          :class="{ 'bg-tan-800': isSelected(item) }"
        >
          <!-- Checkmark for selected -->
          <span class="flex w-5 items-center justify-center">
            <i-fa7-solid-check v-if="isSelected(item)" class="size-3.5 text-green-400" />
            <component
              :is="item.icon"
              v-else-if="item.icon"
              class="size-4 opacity-70 group-hover:opacity-100"
            />
          </span>

          <span class="flex-1 font-medium">{{ item.label }}</span>
        </button>
      </div>
    </transition>
  </div>
</template>
